import React, { ChangeEvent } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  image_error_replace,
  getCookie,
  combine_name,
  check_null_text
} from '../../../util_functions';

import * as actions from '../../../redux/action_creators';

import HighlightSearch from '../../util_components/highlight_search_text';
import './home.css';
import './list.css';
import { CareTicket } from '../../../../myt_chat/src/chat/packets/CareTicket';
import ChatClient from '../../../../myt_chat/src/chat/network/ChatClient';
import { CallbackHandler } from '../../../../myt_chat/src/chat/callbacks/handler';
import ConnectionState from '../../../../myt_chat/src/chat/enums/ConnectionState';
import CareHandler from '../../../../myt_chat/src/chat/network/CareHandler';
import UserRole from '../../../../myt_chat/src/chat/enums/UserRole';
import Message from '../../../../myt_chat/src/chat/models/Message';
import { ChatWindowManager, openChatWithUserUUID } from '../chat';
import MytNotification from '../../../../myt_chat/src/notification';
import { compareTickets } from './utils';

type StudentList = Array<{
  profile_photo: string;
  student_uuid: string;
  sq_fname: string;
  sq_mname: string;
  sq_lname: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  email_verified: number;
  phone_personal: string;
  yoga_level: string;
  credits: string;
}>;

type ChatHomeProps = {
  cms_token: string;
  search_students_list: StudentList;
  student_search_status: string;
  search_students: (item: { search_param: string }) => void;
};

type ChatHomeState = {
  chat_count: number;
  search_text: string;
  search_network: string;
  student_unread: boolean;
  chatLoading: boolean;
  tickets: Array<CareTicket>;
  allTickets: Array<CareTicket>;
  isTerminated: boolean;
  closedTickets: Map<string, boolean>;
  windowFocused: boolean;
  activeChannelUuid: string | null;
};

const sortTickets = (list: CareTicket[]) => list.slice().sort(compareTickets);

class ChatHome extends React.Component<ChatHomeProps, ChatHomeState> {
  state: ChatHomeState = {
    chat_count: 0,
    search_text: '',
    search_network: '',
    student_unread: false,
    chatLoading: true,
    isTerminated: false,
    tickets: [],
    allTickets: [],
    closedTickets: new Map<string, boolean>(),
    windowFocused: true,
    activeChannelUuid: null
  };

  private updateThrottleTimer: any = null;
  private pendingMessages: { [key: string]: Message } = {};
  private firstPendingMessageTime: number | null = null;
  private initializationTimeout: any = null;

  messageListener = (message: Message) => {
    if (message.sender.role !== UserRole.STUDENT) {
      //only showing student tickets
      return;
    }

    // Don't process messages until initial tickets are loaded
    // This prevents race condition on Windows where messages arrive before initial load
    if (this.state.chatLoading) {
      return;
    }

    // Store the message for batched processing
    this.pendingMessages[message.sender.uuid] = message;

    // Clear existing timer
    if (this.updateThrottleTimer) {
      clearTimeout(this.updateThrottleTimer);
    }

    // Batch updates to avoid excessive re-renders
    // But set max wait time to prevent indefinite delays
    const now = Date.now();
    if (!this.firstPendingMessageTime) {
      this.firstPendingMessageTime = now;
    }

    const waitTime = now - this.firstPendingMessageTime > 500 ? 0 : 100;

    this.updateThrottleTimer = setTimeout(() => {
      this.processPendingMessages();
    }, waitTime);
  };

  private processPendingMessages = () => {
    const pendingKeys = Object.keys(this.pendingMessages);
    if (pendingKeys.length === 0) return;

    // Reset the first message time tracker
    this.firstPendingMessageTime = null;

    const items: { [key: string]: CareTicket } = {};

    // Build map from current tickets
    this.state.allTickets.forEach((ticket) => {
      items[ticket.user] = ticket;
    });

    // Optimize: Only filter if there are actually closed tickets to remove
    let updatedClosedTickets = this.state.closedTickets;
    if (this.state.closedTickets.size > 0) {
      const hasClosedToRemove = pendingKeys.some((key) => this.state.closedTickets.has(key));
      if (hasClosedToRemove) {
        updatedClosedTickets = new Map(
          Array.from(this.state.closedTickets.entries()).filter(
            ([key]) => !pendingKeys.includes(key)
          )
        );
      }
    }

    let notificationTicket: CareTicket | null = null;

    // Process all pending messages
    pendingKeys.forEach((userUuid) => {
      const message = this.pendingMessages[userUuid];
      const isActive = this.state.activeChannelUuid === userUuid && this.state.windowFocused;
      const existing = items[userUuid];
      const nextUnread = isActive ? 0 : existing ? Math.max(existing.unread, 0) + 1 : 1;

      const newTicket: CareTicket = {
        user: userUuid,
        name: message.sender.name,
        avatar: message.sender.avatar,
        role: message.sender.role,
        message: message,
        unread: nextUnread
      };

      items[userUuid] = newTicket;

      // Only send notification for the first new message if not active
      if (!notificationTicket && !isActive && nextUnread > 0) {
        notificationTicket = newTicket;
      }
    });

    this.pendingMessages = {};

    const allTickets = sortTickets(Object.keys(items).map((key) => items[key]));

    // Apply search filter directly to avoid nested setState
    const searchQuery = this.state.search_text;
    const filteredTickets = searchQuery
      ? allTickets.filter((ticket) => ticket.name.toLowerCase().includes(searchQuery))
      : allTickets;

    // Single combined setState call
    this.setState(
      {
        allTickets: allTickets,
        tickets: filteredTickets,
        closedTickets: updatedClosedTickets
      },
      () => {
        this.openPendingIfAny();

        // Defer notification to avoid blocking UI on Windows
        if (notificationTicket && !this.state.windowFocused) {
          setTimeout(() => {
            this.sendNotification(notificationTicket!);
          }, 0);
        }
      }
    );
  };

  sendNotification = (ticket: CareTicket) => {
    const { name, message, avatar } = ticket;
    MytNotification.showLocalNotification(
      ticket.name, // tag
      `New message from ${name}: ${message.text || 'You have a new message'}`,
      () => this.on_chat_click(ticket) // click handler
    );
  };

  connectionListener = (state: ConnectionState) => {
    if (state === ConnectionState.AUTHENTICATED) {
      CareHandler.shared.fetchP2PCareTickets(UserRole.STUDENT).then(this.onCareTicketsFetched);

      // Fallback: Force initialization complete after 10 seconds
      this.initializationTimeout = setTimeout(() => {
        if (this.state.chatLoading) {
          console.warn('Chat initialization timeout - forcing complete');
          this.setState({ chatLoading: false });
        }
      }, 10000);
    }
  };

  private pendingChannelUuid: string | null = null;
  private pendingChannelTimeout: any = null;

  componentDidMount() {
    this.setState({ chat_count: Math.floor(window.innerWidth / 390) });
    MytNotification.webPushInit();

    CallbackHandler.shared.registerOnMessageAddedListener(this.messageListener);
    CallbackHandler.shared.registerConnectionListener(this.connectionListener);

    ChatClient.shared.initialize();

    this.connectionListener(ChatClient.shared.connectionState());
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);

    // 1) If the app was redirected here from a push click, pick it up
    try {
      const raw = sessionStorage.getItem('pendingChatOpen');
      if (raw) {
        const { channelUuid } = JSON.parse(raw);
        this.pendingChannelUuid = channelUuid;
        sessionStorage.removeItem('pendingChatOpen');

        // Clear pending after 5 seconds if not found
        this.pendingChannelTimeout = setTimeout(() => {
          if (this.pendingChannelUuid === channelUuid) {
            console.log('Pending channel not found, clearing:', channelUuid);
            this.pendingChannelUuid = null;
          }
        }, 5000);
      }
    } catch (error) {
      console.log('Error pandingChatOpen data', error);
    }

    // 2) If we were already on the chat page and got a push, handle custom event
    window.addEventListener('chat:open', this.onChatOpenEvt);
    window.addEventListener('chat:closed', this.onChatClosedEvt);

    try {
      if (navigator.serviceWorker) {
        try {
          navigator.serviceWorker.addEventListener('message', (event) => {
            const { type, role, channelUuid } = event.data || {};
            if (type !== 'CHAT_CLICK' && !channelUuid) return;

            if (type === 'CHAT_CLICK') {
              const { channelUuid } = event.data;
              const ticket = this.state.allTickets.find((t) => t.user === channelUuid);
              if (ticket) {
                this.on_chat_click(ticket);
              }
            }
          });
        } catch (error) {
          console.log('Error in navigator block in teacher', error);
        }
      }
    } catch (error) {
      console.log('Error in navigator service worker in teacher block', error);
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('channel');
      if (q) this.pendingChannelUuid = q;
    } catch {}
  }

  componentWillUnmount() {
    CallbackHandler.shared.unregisterOnMessageAddedListener(this.messageListener);
    CallbackHandler.shared.unregisterConnectionListener(this.connectionListener);
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    window.removeEventListener('chat:open', this.onChatOpenEvt);
    window.removeEventListener('chat:closed', this.onChatClosedEvt);

    // Clean up throttle timers
    if (this.updateThrottleTimer) {
      clearTimeout(this.updateThrottleTimer);
      this.updateThrottleTimer = null;
    }
    if (this.searchThrottleTimer) {
      clearTimeout(this.searchThrottleTimer);
      this.searchThrottleTimer = null;
    }
    if (this.pendingChannelTimeout) {
      clearTimeout(this.pendingChannelTimeout);
      this.pendingChannelTimeout = null;
    }
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }
  }

  private onChatOpenEvt = (e: any) => {
    const channelUuid = e?.detail?.channelUuid;
    if (channelUuid) this.openChannelIfPresent(channelUuid);
  };

  private openPendingIfAny() {
    if (!this.pendingChannelUuid) return;
    const ch = this.pendingChannelUuid;
    const ticket = this.state.allTickets.find((t) => t.user === ch);
    if (ticket) {
      this.pendingChannelUuid = null; // clear only when found
      if (this.pendingChannelTimeout) {
        clearTimeout(this.pendingChannelTimeout);
        this.pendingChannelTimeout = null;
      }
      this.on_chat_click(ticket);
    }
  }
  private openChannelIfPresent = (channelUuid: string) => {
    const ticket = this.state.allTickets.find((t) => t.user === channelUuid);
    if (ticket) this.on_chat_click(ticket);
  };

  private onChatClosedEvt = (e: any) => {
    const { channelUuid, userUuid } = e?.detail ?? {};
    if (!channelUuid && !userUuid) return;

    // Our tickets are keyed by student user uuid (t.user). Prefer that.
    const key = userUuid ?? channelUuid;

    // If the closed chat was the active one, clear it (we track active by user uuid)
    const nextActive = this.state.activeChannelUuid === key ? null : this.state.activeChannelUuid;

    // Zero unread for that user - only update if needed
    let hasChanges = false;
    const allTickets = this.state.allTickets.map((t) => {
      if (t.user === key && t.unread > 0) {
        hasChanges = true;
        return { ...t, unread: 0 };
      }
      return t;
    });

    // Only update state if something changed
    if (hasChanges || nextActive !== this.state.activeChannelUuid) {
      // Update filtered tickets only if all tickets changed
      const tickets = hasChanges
        ? this.state.tickets.map((t) => {
            if (t.user === key && t.unread > 0) {
              return { ...t, unread: 0 };
            }
            return t;
          })
        : this.state.tickets;

      this.setState({ allTickets, tickets, activeChannelUuid: nextActive });
    }
  };

  onWindowFocus = () => {
    this.setState({ windowFocused: true }, this.markActiveAsRead);
  };

  markActiveAsRead = () => {
    const active = this.state.activeChannelUuid;
    if (!active) return;

    // Check if there's actually anything to update
    let hasUnread = false;
    for (let i = 0; i < this.state.allTickets.length; i++) {
      if (this.state.allTickets[i].user === active && this.state.allTickets[i].unread > 0) {
        hasUnread = true;
        break;
      }
    }

    if (!hasUnread) return; // No need to update if nothing to change

    const allTickets = this.state.allTickets.map((t) =>
      t.user === active ? { ...t, unread: 0 } : t
    );
    const tickets = this.state.tickets.map((t) => (t.user === active ? { ...t, unread: 0 } : t));

    this.setState({ allTickets, tickets });
  };

  onWindowBlur = () => {
    this.setState({ windowFocused: false });
  };
  onCareTicketsFetched = (result: { tickets: Array<CareTicket> }) => {
    // Clear initialization timeout
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
      this.initializationTimeout = null;
    }

    // Use the shared compareTickets function for consistency
    const tickets = sortTickets(result.tickets);

    this.setState(
      {
        allTickets: tickets,
        chatLoading: false
      },
      () => {
        this.updateTickets(tickets, '');
      }
    );
  };

  updateTicketsInternal = (tickets: Array<CareTicket>, searchQuery: string) => {
    let filtered = tickets.filter((ticket) => {
      return ticket.name.toLowerCase().includes(searchQuery);
    });

    this.setState({
      tickets: filtered,
      search_text: searchQuery
    });
  };

  updateTickets = (tickets: Array<CareTicket>, searchQuery: string) => {
    this.updateTicketsInternal(tickets, searchQuery);
    this.openPendingIfAny();
  };

  private searchThrottleTimer: any = null;

  filterTickets = (e: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = e.target.value.toLowerCase().trim();

    // Clear existing timer
    if (this.searchThrottleTimer) {
      clearTimeout(this.searchThrottleTimer);
    }

    // Update search text immediately for UI feedback
    this.setState({ search_text: searchQuery });

    // Throttle the actual filtering to avoid blocking
    this.searchThrottleTimer = setTimeout(() => {
      this.updateTickets(this.state.allTickets, searchQuery);
    }, 150);
  };

  on_chat_click = (ticketItem: CareTicket) => {
    // Immediately process any pending messages before opening chat
    if (Object.keys(this.pendingMessages).length > 0) {
      if (this.updateThrottleTimer) {
        clearTimeout(this.updateThrottleTimer);
        this.updateThrottleTimer = null;
      }
      this.processPendingMessages();
    }

    const active = ticketItem.user;

    const allTickets = this.state.allTickets.map((t) =>
      t.user === active ? { ...t, unread: 0 } : t
    );
    const tickets = this.state.tickets.map((t) => (t.user === active ? { ...t, unread: 0 } : t));

    this.setState({ allTickets, tickets, activeChannelUuid: active });

    openChatWithUserUUID(active, UserRole.STUDENT);
  };

  render_single_student = (item: CareTicket, index: number) => {
    return (
      <div className="ch-sc-cb" key={index + item.user} onClick={() => this.on_chat_click(item)}>
        <div className="ch-sc-cb-i">
          <img src={item.avatar} alt="pic" onError={image_error_replace} />
        </div>
        <p className="ch-p">{item.name}</p>
        {item.unread > 0 ? <div className="ch-sc-bd">{item.unread}</div> : null}

        {item.unread === 0 ? (
          <>
            {!this.state.closedTickets.has(item.user) ? (
              <div className="close-tckt">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    let res = window.confirm(
                      `Are you sure you want to close the ticket with ${item.name}?`
                    );
                    if (res) {
                      CareHandler.shared
                        .closeP2PCareTicket(item.user, Date.now() * 1000)
                        .then(() => {
                          // Create new Map instance to avoid mutation
                          const updatedClosedTickets = new Map(this.state.closedTickets);
                          updatedClosedTickets.set(item.user, true);
                          this.setState({
                            closedTickets: updatedClosedTickets
                          });
                        });
                    }
                  }}
                >
                  Close Ticket
                </button>
              </div>
            ) : (
              <div className="close-tckt">Ticket Closed</div>
            )}
          </>
        ) : null}
      </div>
    );
  };

  ignoreCatch = (error) => {
    console.log(error);
  };

  render_student_chats = () => {
    if (this.state.chatLoading) {
      return <h2>Chat Initializing. Please wait...</h2>;
    }
    return (
      <>
        <div className="ch-sc-h">
          <p>Student chats - {this.state.allTickets.length}</p>
          <input
            value={this.state.search_text}
            name="student_search"
            placeholder="Enter name to search"
            onChange={this.filterTickets}
          />
        </div>
        {this.state.search_text !== '' ? (
          <div
            style={{
              textAlign: 'center',
              fontSize: '18px',
              padding: '20px',
              border: '1px solid',
              borderRight: 'none'
            }}
          >
            Search whole database for{' '}
            <span
              style={{ cursor: 'pointer', color: 'blue' }}
              onClick={() => {
                this.setState(
                  {
                    search_network: this.state.search_text
                  },
                  () => {
                    if (this.state.search_network.length > 2)
                      this.props.search_students({ search_param: this.state.search_network });
                  }
                );
              }}
            >
              {' '}
              '{this.state.search_text}' {'-->'}
            </span>{' '}
          </div>
        ) : null}
        <div className="ch-sc-ch">{this.state.tickets.map(this.render_single_student)}</div>
      </>
    );
  };

  render_chat_container = () => {
    return (
      <div className="ch-c">
        <div className="ch-sc">{this.render_student_chats()}</div>

        {this.state.search_network !== '' ? (
          <div className="ch-sc">
            <div style={{ textAlign: 'center', fontSize: '18px', padding: '20px' }}>
              Database search results for '
              <span style={{ cursor: 'pointer', color: 'blue' }}>{this.state.search_network}</span>'
            </div>
            {this.render_display_text()}
          </div>
        ) : null}
        {this.state.isTerminated ? (
          <div style={{ textAlign: 'center', fontSize: '18px', padding: '20px' }}>
            Hridaya Chat is opened on a new browser tab, hence the service is disconnected here.
            Please refresh this window if you want to resume chat service on this browser tab.
          </div>
        ) : null}
      </div>
    );
  };

  render_display_text = () => {
    const search_value = this.state.search_network.trim();

    if (search_value.length >= 3 && this.props.student_search_status === 'success') {
      return this.render_students();
    }

    let display_text = '';

    if (search_value.length === 0) {
      display_text = 'Enter name, email or phone number to search';
    } else if (search_value.length > 0 && search_value.length < 3) {
      display_text = 'Please type atleast 3 letters to search';
    } else if (this.props.student_search_status === 'loading') {
      display_text = 'Loading...';
    }

    return <div className="sl-display-cont">{display_text}</div>;
  };

  render() {
    if (!!!this.props.cms_token) {
      return <Redirect to="/" />;
    }
    return (
      <>
        <div className="ch">
          <Helmet>
            <meta charSet="utf-8" />
            <title>All Chats</title>
          </Helmet>
          <div className="ch-h" style={{ justifyContent: 'start' }}>
            <a href="/">
              <img src="https://images.myyogateacher.com/banyan-logo-200.png" alt="logo-1" />
            </a>
            <p style={{ marginLeft: '20px' }}>All chats</p>
          </div>
          {this.render_chat_container()}
        </div>
        <ChatWindowManager />
      </>
    );
  }

  render_students = () => {
    let students = [...this.props.search_students_list].filter(
      (x) => 'student_uuid' in x && x.student_uuid !== ''
    );

    return (
      <div className="cus-table">
        <div className="ct-row ct-h">
          <div className="ct-col ct-qf">
            <img src="" alt="i" onError={image_error_replace} />
          </div>
          <div className="ct-col">Student</div>

          {/* <div className="ct-col">USER TYPE</div> */}
          <div className="ct-col">PHONE</div>
        </div>
        {students.map((student, index) => {
          const profile_photo = !!student.profile_photo ? student.profile_photo : '';
          const tr_class = !!student.student_uuid
            ? 'ct-row ct-cur tr-cursor-row'
            : 'ct-row tr-no-uuid';
          const sq_name = combine_name(student.sq_fname, student.sq_mname, student.sq_lname).trim();
          const name = combine_name(
            student.first_name,
            student.middle_name,
            student.last_name
          ).trim();
          return (
            <div
              className={tr_class}
              key={student.student_uuid}
              onClick={() => {
                openChatWithUserUUID(student.student_uuid, UserRole.STUDENT);
              }}
              data-id={student.student_uuid}
            >
              <div className="ct-col ct-qf">
                <img src={profile_photo} alt="i" onError={image_error_replace} />
              </div>
              <div className="ct-col ct-bw">
                <div style={{ fontSize: '17px' }}>
                  <HighlightSearch
                    full_text={name ?? sq_name}
                    search_text={this.state.search_network}
                  />
                </div>
                {!!sq_name && sq_name !== 'User' && sq_name !== name ? (
                  <div style={{ fontSize: '14px', opacity: '0.7' }}>
                    <HighlightSearch full_text={sq_name} search_text={this.state.search_network} />
                  </div>
                ) : null}
              </div>
              <div className="ct-col ct-si">
                {student.email_verified === 1 ? (
                  <img src="https://images.myyogateacher.com/icons/ic-checkmark-48.png" alt="av" />
                ) : (
                  <img src="https://images.myyogateacher.com/icons/ic-delete-48.png" alt="nav" />
                )}
              </div>

              <div className="ct-col ct-ho">
                <HighlightSearch
                  full_text={check_null_text(student.phone_personal)}
                  search_text={this.state.search_network}
                />
              </div>
              <div className="ct-col ct-ho">{student.yoga_level}</div>
              <div className="ct-col ct-ho ct-hf">{student.credits}</div>
            </div>
          );
        })}
      </div>
    );
  };
}

type GlobalState = {
  home: {
    cms_token: string;
  };
  students: {
    search_students_list: StudentList;
  };
  loading: {
    student_search_status: string;
  };
};

const mapStateToProps = (state: GlobalState) => {
  return {
    cms_token: state.home.cms_token,
    search_students_list: state.students.search_students_list,
    student_search_status: state.loading.student_search_status
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    search_students: (payload: { search_param: string }) => {
      dispatch(actions.search_students(payload));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatHome);
